import { type Action, createStore } from 'react-sweet-state';
import type { RangeRef } from 'slate';

import type {
  SavedDocumentAnchor,
  SavedDocumentRating,
  SavedDocumentTextRating,
  SavedDocumentThread,
} from 'src/data/savedDocument';
import {
  createSavedDocumentTextRating,
  createSavedDocumentThread,
  deleteSavedDocumentComment,
  deleteSavedDocumentTextRating,
  querySavedDocument,
  replyToSavedDocumentThread,
  resolveSavedDocumentThread,
  updateSavedDocumentComment,
  updateSavedDocumentTextRating,
} from 'src/graphql/savedDocument';
import { isGraphQLAccessError } from 'src/lib/graphql';
import { captureMessage } from 'src/lib/sentry';
import type { VoidFunction } from 'src/lib/types';

import type { ElementOffset } from '../lib/utils';

export interface SavedDocumentCommentDraft {
  // Where the comment was started. The range follows the edits made while it is written.
  anchor: SavedDocumentAnchor;
  rangeRef: RangeRef;
  position: ElementOffset;
}

export const SAVED_DOCUMENT_PANEL_TYPE = {
  thread: 'thread',
  ratings: 'ratings',
  bubble: 'bubble',
} as const;

// The text a panel opens from, as an offset within the document. The panel sits below or above it.
export type SavedDocumentPanelAnchor = Pick<ElementOffset, 'top' | 'bottom'>;

// `key` is a thread id or a rating group key, by `type`. A bubble's panel opens from the bubble, so
// it has no anchor of its own. It keeps the highlights it opened with, as bubbles regroup.
export type SavedDocumentOpenPanel =
  | {
      type: typeof SAVED_DOCUMENT_PANEL_TYPE.thread;
      key: string;
      anchor: SavedDocumentPanelAnchor;
    }
  | {
      type: typeof SAVED_DOCUMENT_PANEL_TYPE.ratings;
      key: string;
      anchor: SavedDocumentPanelAnchor;
    }
  | { type: typeof SAVED_DOCUMENT_PANEL_TYPE.bubble; targets: string[] };

export interface State {
  threads: SavedDocumentThread[];
  textRatings: SavedDocumentTextRating[];
  fetchToken: number;
  draft: SavedDocumentCommentDraft | null;
  // Kept apart from `draft`: the editor redraws its highlights whenever `draft` changes.
  draftHasText: boolean;
  openPanel: SavedDocumentOpenPanel | null;
  creatingThread: boolean;
  replyingThreadId: string | null;
}

export interface ContainerProps {
  projectId: string;
  onAccessLost?: VoidFunction;
}

export type Actions = typeof ACTIONS;

type StoreAction<TResult = void> = Action<State, ContainerProps, TResult>;

const INITIAL_STATE: State = {
  threads: [],
  textRatings: [],
  fetchToken: 0,
  draft: null,
  draftHasText: false,
  openPanel: null,
  creatingThread: false,
  replyingThreadId: null,
};

/**
 * Runs a mutation, then refetches the document, as the mutations return no document. A lost access
 * goes to `onAccessLost` when the host passes one. Other errors go back to the caller.
 */
const mutate =
  <TResult>(run: () => Promise<TResult>): StoreAction<Promise<TResult | undefined>> =>
  async ({ dispatch }, { onAccessLost }) => {
    try {
      const result = await run();
      await dispatch(ACTIONS.fetchSavedDocument());
      return result;
    } catch (error) {
      if (onAccessLost && isGraphQLAccessError(error as Error)) {
        onAccessLost();
        return undefined;
      }
      throw error;
    }
  };

export const PRIVATE_ACTIONS = {
  init:
    (): StoreAction =>
    ({ setState }) => {
      setState(INITIAL_STATE);
    },
};

const ACTIONS = {
  // The polling and every own mutation call this. A newer call makes an older response stale.
  fetchSavedDocument:
    (): StoreAction<Promise<void>> =>
    async ({ getState, setState }, { projectId, onAccessLost }) => {
      const fetchToken = getState().fetchToken + 1;
      setState({ fetchToken });

      try {
        const savedDocument = await querySavedDocument({ id: Number(projectId) });
        if (getState().fetchToken !== fetchToken) {
          return;
        }
        if (!savedDocument) {
          onAccessLost?.();
          return;
        }
        setState({ threads: savedDocument.threads, textRatings: savedDocument.textRatings });
      } catch (error) {
        if (onAccessLost && isGraphQLAccessError(error as Error)) {
          onAccessLost();
          return;
        }
        captureMessage('Failed to fetch the saved document', {
          level: 'warning',
          extra: { error, projectId },
        });
      }
    },

  createThread:
    (anchor: SavedDocumentAnchor, text: string): StoreAction<Promise<string | undefined>> =>
    async ({ setState, dispatch }, { projectId }) => {
      setState({ creatingThread: true });
      try {
        const thread = await dispatch(
          mutate(() => createSavedDocumentThread({ projectId, anchor, text }))
        );
        return thread?.id;
      } finally {
        setState({ creatingThread: false });
      }
    },

  replyToThread:
    (threadId: string, text: string): StoreAction<Promise<void>> =>
    async ({ setState, dispatch }) => {
      setState({ replyingThreadId: threadId });
      try {
        await dispatch(mutate(() => replyToSavedDocumentThread({ threadId, text })));
      } finally {
        setState({ replyingThreadId: null });
      }
    },

  resolveThread:
    (threadId: string): StoreAction<Promise<void>> =>
    async ({ dispatch }) => {
      await dispatch(mutate(() => resolveSavedDocumentThread({ threadId })));
    },

  updateComment:
    (commentId: string, text: string): StoreAction<Promise<void>> =>
    async ({ dispatch }) => {
      await dispatch(mutate(() => updateSavedDocumentComment({ commentId, text })));
    },

  deleteComment:
    (commentId: string): StoreAction<Promise<void>> =>
    async ({ dispatch }) => {
      await dispatch(mutate(() => deleteSavedDocumentComment({ commentId })));
    },

  createTextRating:
    (anchor: SavedDocumentAnchor, value: SavedDocumentRating): StoreAction<Promise<void>> =>
    async ({ dispatch }, { projectId }) => {
      await dispatch(mutate(() => createSavedDocumentTextRating({ projectId, anchor, value })));
    },

  updateTextRating:
    (id: string, value: SavedDocumentRating): StoreAction<Promise<void>> =>
    async ({ dispatch }) => {
      await dispatch(mutate(() => updateSavedDocumentTextRating({ id, value })));
    },

  deleteTextRating:
    (id: string): StoreAction<Promise<void>> =>
    async ({ dispatch }) => {
      await dispatch(mutate(() => deleteSavedDocumentTextRating({ id })));
    },

  setDraft:
    (draft: SavedDocumentCommentDraft): StoreAction =>
    ({ getState, setState }) => {
      getState().draft?.rangeRef.unref();
      setState({ draft, draftHasText: false, openPanel: null });
    },

  setDraftPosition:
    (position: ElementOffset): StoreAction =>
    ({ getState, setState }) => {
      const { draft } = getState();
      if (draft) {
        setState({ draft: { ...draft, position } });
      }
    },

  setDraftHasText:
    (draftHasText: boolean): StoreAction =>
    ({ setState }) => {
      setState({ draftHasText });
    },

  getDraft:
    (): StoreAction<State['draft']> =>
    ({ getState }) =>
      getState().draft,

  setOpenPanel:
    (openPanel: SavedDocumentOpenPanel): StoreAction =>
    ({ getState, setState }) => {
      getState().draft?.rangeRef.unref();
      setState({ openPanel, draft: null, draftHasText: false });
    },

  closePanels:
    (): StoreAction =>
    ({ getState, setState }) => {
      getState().draft?.rangeRef.unref();
      setState({ openPanel: null, draft: null, draftHasText: false });
    },

  // A comment the user started to write stays open until it is posted, cancelled or escaped. A
  // click elsewhere does not close it or open another panel over it.
  openPanelUnlessDraftHasText:
    (openPanel: SavedDocumentOpenPanel): StoreAction =>
    ({ getState, dispatch }) => {
      if (!getState().draftHasText) {
        dispatch(ACTIONS.setOpenPanel(openPanel));
      }
    },

  closePanelsUnlessDraftHasText:
    (): StoreAction =>
    ({ getState, dispatch }) => {
      if (!getState().draftHasText) {
        dispatch(ACTIONS.closePanels());
      }
    },
};

export const SavedDocumentViewStore = createStore<State, Actions, ContainerProps>({
  initialState: INITIAL_STATE,
  actions: ACTIONS,
  name: 'SavedDocumentView',
});
